import React, { ReactFCWithChildren } from 'react';
import { Option, GiftCard, OrderItem, GiftExperienceCode, OrderItemChangeRequest } from 'src/types';
import { displayTimeslot, getTotalQty, scrollTo } from 'src/helpers';
import { truncate } from 'lodash-es';
import useConfirmDialog from 'src/hooks/useConfirmDialog';
import Link from 'next/link';
import { AppRoutes } from 'src/lib/routes';
import { useRouter } from 'next/router';
import ImageWithFallback from 'src/components/ImageWithFallback';
import ApiClient from 'src/lib/api_client';
import useCart from 'src/hooks/useCart';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import dayjs from 'dayjs';
import { LOCALE_DATE_FORMAT, UITimeFormat } from 'src/lib/constants';
import classNames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';
import DisplayStartEndDate from 'src/components/DisplayStartEndDate';

type Props = {
  option?: Option | null;
  giftCard?: GiftCard | null;
  isCheckout?: boolean | null;
  isInsideCartPopup?: boolean | null;
  orderItem: OrderItem;
  orderItemChangeRequest?: OrderItemChangeRequest;
  renderAfterQuantity?: () => JSX.Element;
  giftExperienceCode?: GiftExperienceCode;
};

const PurchaseItem: ReactFCWithChildren<Props> = ({
  isCheckout,
  isInsideCartPopup,
  option,
  giftCard,
  orderItem,
  children,
  renderAfterQuantity,
  orderItemChangeRequest,
  giftExperienceCode
}) => {
  const locale = useLocale();
  const translation = useTranslations('components.PurchaseItem')
  const UIDateFormat = LOCALE_DATE_FORMAT[locale].UIDateFormat
  const clearCart = useEnhanceApiClient(ApiClient.clearCart);
  const confirmDialog = useConfirmDialog();
  const router = useRouter();
  const [cart, setCart] = useCart();

  const styleText = isInsideCartPopup
    ? 'tw-font-sans tw-font-normal tw-text-sm'
    : 'tw-font-styleSans tw-font-semibold';

  return (
    <div className={classNames({"tw-bg-white tw-rounded-lg": !isInsideCartPopup})}>
      {option ? (
        <div
          className={classNames("img-as-bg tw-h-52 tw-w-full", {
            "tw-rounded-lg tw-border-2 tw-border-brand-green tw-border-solid": isInsideCartPopup,
            "tw-rounded-tl-lg tw-rounded-tr-lg": !isInsideCartPopup
          })}
          style={{ backgroundImage: `url(${option.images[0]?.mdUrl})` }}
        ></div>
      ) : giftCard && orderItem.giftImage ? (
        <div className="gift-card__img gift-card__img--checkout">
          <div className="tw-h-52 md:tw-h-60 tw-w-full tw-relative">
            <ImageWithFallback
              className={classNames({"tw-rounded-lg tw-border-2 tw-border-brand-green tw-border-solid": isInsideCartPopup })}
              record={orderItem.giftImage}
              imgField="webpMdUrl"
              alt=""
              fill
            />
          </div>
        </div>
      ) : (
        ''
      )}

      <div className={classNames({
        "tw-p-4 tw-border tw-border-grey-lighter tw-border-solid tw-rounded-bl-lg tw-rounded-br-lg": !isInsideCartPopup,
        "tw-py-4": isInsideCartPopup
      })}>
        <div className={classNames("tw-pb-4 tw-mb-4", {"tw-border-b tw-border-grey-lighter tw-border-solid": !isInsideCartPopup}
        )}>
          {orderItem.isGift ? (
            <>
              {orderItem.isGiftExperience && option ? (
                <>
                  <div className="tw-mb-2 tw-font-styleSans tw-font-semibold tw-text-xl tw-truncate tw-leading-none">[{translation('gift')}] {option.name}</div>

                  {renderAfterQuantity ? renderAfterQuantity() : ''}

                  {isCheckout && (
                    <div>
                      <Link legacyBehavior prefetch={false}                         href={AppRoutes.experienceRoute(
                          {
                            id: option.slugCode,
                          },
                          { gift_experience: 1 }
                        ).toPath()}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <a className={`tw-text-brand-navy tw-underline tw-cursor-pointer ${styleText}`}>{translation('edit')}</a>
                      </Link>
                      <span className={`tw-mx-2 tw-text-brand-navy ${styleText}`}>|</span>
                      <span
                        className={`tw-text-brand-navy tw-underline tw-cursor-pointer ${styleText}`}
                        onClick={() => {
                          confirmDialog({
                            title: translation('cart'),
                            body: translation('removeFromCart'),
                            okText: translation('yes'),
                            cancelText: translation('no'),
                          }).then(async () => {
                            const [json] = await clearCart({ cart_id: cart.id });
                            if (!('error' in json)) {
                              setCart(json);
                              window.scrollTo({ top: 0 })
                            } else {
                              throw new Error(`${translation('couldnotClearCart')} #${cart.id}`);
                            }
                          });
                        }}
                      >
                        {translation('remove')}
                      </span>
                    </div>
                  )}
                </>
              ) : giftCard ? (
                <>
                  <div className="tw-font-styleSans tw-font-semibold tw-mb-2 tw-truncate tw-leading-none">{giftCard.name}</div>

                  <div className="tw-mb-2">{translation('quantity')}: {orderItem.quantity}</div>
                  <div className="tw-mb-2">{translation('dateToSendGift')}: {dayjs(orderItem.sendGiftDate).format(UIDateFormat)} </div>

                  {isCheckout && (
                    <div>
                      <Link legacyBehavior prefetch={false} href={AppRoutes.giftsSendRoute().toPath()}>
                        <a className={`tw-text-brand-navy tw-underline tw-cursor-pointer ${styleText}`}>{translation('edit')}</a>
                      </Link>
                      <span className={`tw-mx-2 tw-text-brand-navy ${styleText}`}>|</span>
                      <span
                        className={`tw-text-brand-navy tw-underline tw-cursor-pointer ${styleText}`}
                        onClick={() => {
                          confirmDialog({
                            title: translation('cart'),
                            body: translation('removeFromCart'),
                            okText: translation('yes'),
                            cancelText: translation('no'),
                          }).then(async () => {
                            const [json] = await clearCart({ cart_id: cart.id });
                            if (!('error' in json)) {
                              setCart(json);
                              window.scrollTo({ top: 0 })
                            } else {
                              throw new Error(`${translation('couldnotClearCart')} #${cart.id}`);
                            }
                          });
                        }}
                      >
                        {translation('remove')}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                ''
              )}
            </>
          ) : option ? (
            <>
              <div className="tw-mb-1">
                <div className="tw-font-semibold tw-font-styleSans tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none">
                  {truncate(option.name, { length: 42 })}
                </div>
              </div>

              <div className={`tw-mb-2 tw-text-brand-navy ${styleText}`}>
                {orderItemChangeRequest ? (
                  orderItemChangeRequest.kind === 'change_date' ? (
                    <>
                      <DisplayStartEndDate
                        startDate={orderItemChangeRequest.startDate}
                        endDate={orderItemChangeRequest.endDate}
                      />
                      {orderItemChangeRequest.startTime && orderItemChangeRequest.endTime
                        ? `, ${dayjs.utc(orderItemChangeRequest.startTime).format(UITimeFormat)}`
                        : ''}
                      {' | '}
                      {translation('pluralizePerson', {count: orderItem.totalQuantity})}
                    </>
                  ) : (
                    <>
                      <DisplayStartEndDate
                        startDate={orderItem.startDate}
                        endDate={orderItem.endDate}
                      />
                      {orderItem.startTime && orderItem.endTime
                        ? `, ${dayjs.utc(orderItem.startTime).format(UITimeFormat)}`
                        : ''}
                      {' | '}
                      {translation('pluralizePerson', {count: getTotalQty(orderItemChangeRequest) - orderItem.totalQuantity})}
                    </>
                  )
                ) : (
                  <>
                    <DisplayStartEndDate
                      startDate={orderItem.startDate}
                      endDate={orderItem.endDate}
                    />
                    {orderItem.startTime && orderItem.endTime
                      ? `, ${dayjs.utc(orderItem.startTime).format(UITimeFormat)}`
                      : ''}
                    {' | '}
                    {translation('pluralizePerson', {count: orderItem.totalQuantity})}
                  </>
                )}
              </div>

              {renderAfterQuantity ? renderAfterQuantity() : ''}

              {isCheckout && (
                <div>
                  <Link legacyBehavior prefetch={false}                     href={AppRoutes.experienceRoute({ id: option.slugCode }, { gdc: giftExperienceCode?.code }).toPath()} target="_blank"
                    rel="noreferrer"
                  >
                    <a className={`tw-text-brand-navy tw-underline tw-cursor-pointer ${styleText}`}>
                      {translation('edit')}
                    </a>
                  </Link>
                  <span className={`tw-mx-2 tw-text-brand-navy ${styleText}`}>|</span>
                  <span
                    className={`tw-text-brand-navy tw-underline tw-cursor-pointer ${styleText}`}
                    onClick={() => {
                      confirmDialog({
                        title: translation('cart'),
                        body: translation('removeFromCart'),
                        okText: translation('yes'),
                        cancelText: translation('no'),
                      }).then(async () => {
                        const [json] = await clearCart({ cart_id: cart.id });
                        if (!('error' in json)) {
                          setCart(json);
                          window.scrollTo({ top: 0 })
                        } else {
                          throw new Error(`${translation('couldnotClearCart')} #${cart.id}`);
                        }
                      });
                    }}
                  >
                    {translation('remove')}
                  </span>
                </div>
              )}
            </>
          ) : (
            ''
          )}
        </div>

        {children}
      </div>
    </div>
  );
};

export default PurchaseItem;
